import { Component, OnInit, Output, EventEmitter, ChangeDetectorRef, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TsInfoService } from '../../services/ts-info.service';
import { TimesheetService } from '../../../services/timesheet.service';
import { AuthService } from '../../../services/auth.service';
import { EncryptionService } from '../../../services/encryption.service';
import { Base64UrlService } from '../../../services/base64-url.service';
import {
  AscBasicInfo,
  TimesheetBasicInfo,
  TimesheetOverview,
  TimesheetLineItem,
  TimesheetSummary,
  TimesheetComments
} from '../../models/ts-info.model';

@Component({
  selector: 'app-asc-tsinfo',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './asc-tsinfo.html',
  styleUrls: ['./asc-tsinfo.scss']
})
export class AscTsinfoComponent implements OnInit, AfterViewChecked {
  // 検索条件
  userCode = '';
  ascUq = '';

  // 年月リスト
  yearMonthList: string[] = [];
  selectedYearMonth = '';

  // 基準年月（初期表示・クリア時に使用。ASC一覧出力等の遷移元から引き継ぐ）
  private referenceYearMonth = '';

  // ASC情報
  ascInfo: AscBasicInfo | null = null;

  // タイムシート一覧
  timesheetList: TimesheetBasicInfo[] = [];

  // 選択されたタイムシート情報
  selectedTimesheet: TimesheetOverview | null = null;
  lineItems: TimesheetLineItem[] = [];
  summary: TimesheetSummary | null = null;
  comments: TimesheetComments | null = null;

  // 表示フラグ
  showMessage = false;
  showLinks = false;
  showDetail = false;

  // メッセージ
  message = '';

  // 管理者レベル
  adminLevel = 0;

  private stickyTopsLastUpdate = '';

  // 書き込み権限
  readWrite = false;

  // ナビゲーション用イベントエミッター
  @Output() navigateToJobOrder = new EventEmitter<number>();
  @Output() navigateToAsc = new EventEmitter<{ userCode?: string; ascUq?: string }>();
  @Output() navigateToCustomer = new EventEmitter<number>();
  @Output() proxyRequested = new EventEmitter<{ operation: 'return' | 'submit' | 'approve'; encryptedId: string; encryptedUserId: string }>();

  constructor(
    private tsInfoService: TsInfoService,
    private timesheetService: TimesheetService,
    private authService: AuthService,
    private encryptionService: EncryptionService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef
  ) { }

  ngOnInit(): void {
    // 初期表示：基準年月（遷移前に設定済みの場合はそれを使用、なければ当月）
    const today = new Date();
    if (!this.referenceYearMonth) {
      this.referenceYearMonth = this.getDefaultYearMonth();
    }
    this.selectedYearMonth = this.referenceYearMonth;
    this.generateYearMonthList();

    // AdminLevel と ReadWrite を取得
    this.adminLevel = this.authService.getAdminLevel() || 0;
    this.readWrite = this.authService.getReadWrite();
  }

  ngAfterViewChecked(): void {
    this.updateStickyTops();
  }

  // 各sticky要素の高さを計測しCSS変数にセット
  private updateStickyTops(): void {
    const host = this.el.nativeElement as HTMLElement;
    const topArea = host.querySelector('.top-area') as HTMLElement | null;
    const overview = host.querySelector('.overview-container') as HTMLElement | null;
    const h3 = host.querySelector('.timesheet-area h3') as HTMLElement | null;

    const topH = topArea?.offsetHeight ?? 0;
    const overviewH = overview?.offsetHeight ?? 0;
    const h3H = h3?.offsetHeight ?? 0;

    const key = `${topH}-${overviewH}-${h3H}`;
    if (key === this.stickyTopsLastUpdate) return; // 変化なければスキップ
    this.stickyTopsLastUpdate = key;

    host.style.setProperty('--top-area-h', `${topH}px`);
    host.style.setProperty('--overview-top', `${topH}px`);
    host.style.setProperty('--h3-top', `${topH + overviewH}px`);
    host.style.setProperty('--thead-top', `${topH + overviewH + h3H}px`);
  }

  // 登録番号の自動フォーマット (XXX-XX-XXXX)
  onUserCodeInput(event: Event): void {
    if ((event as InputEvent).isComposing) return;
    const input = event.target as HTMLInputElement;
    let value = input.value;
    const digitsOnly = value.replace(/\D/g, '');
    
    let formatted = '';
    if (digitsOnly.length > 0) {
      formatted = digitsOnly.substring(0, 3);
      if (digitsOnly.length > 3) {
        formatted += '-' + digitsOnly.substring(3, 5);
      }
      if (digitsOnly.length > 5) {
        formatted += '-' + digitsOnly.substring(5, 9);
      }
    }
    
    // DOM を直接更新してから ngModel を更新する
    // （[(ngModel)] の DefaultValueAccessor が同じ input イベントで
    //   onChange(input.value) を呼び出し raw 値で上書きするのを防ぐため）
    input.value = formatted;
    this.userCode = formatted;
    
    // ASC UQをクリア
    if (this.userCode) {
      this.ascUq = '';
    }
  }

  // ASC UQ入力時のハンドラ
  onAscUqInput(): void {
    // 登録番号をクリア
    if (this.ascUq) {
      this.userCode = '';
    }
  }

  // 表示ボタンクリック
  onDisplay(): void {
    this.clearResults();
    
    // 検索条件チェック
    if (!this.userCode && !this.ascUq) {
      return;
    }

    // ASC情報を取得
    // FIX: ascUq がある場合は userId のみ送信（userCode を渡すとAPIゲートウェイで404になる）
    const userId = this.ascUq ? parseInt(this.ascUq) : undefined;
    
    this.tsInfoService.getAscInfo(
      this.ascUq ? undefined : (this.userCode || undefined),
      userId
    ).subscribe({
      next: (asc) => {
        if (!asc) {
          this.message = 'この番号のASCは存在しません。';
          this.showMessage = true;
          return;
        }

        this.ascInfo = asc;
        this.generateYearMonthList();
        // 表示ボタン押下時は選択中の年月を維持する（当月にリセットしない）
        // selectedYearMonth がリストに含まれていない場合のみ先頭に合わせる
        if (!this.yearMonthList.includes(this.selectedYearMonth)) {
          this.selectedYearMonth = this.yearMonthList[0];
        }
        this.cdr.detectChanges();
        this.loadTimesheets();
      },
      error: (error) => {
        this.message = 'この番号のASCは存在しません。';
        this.showMessage = true;
      }
    });
  }

  // クリアボタンクリック
  onClear(): void {
    this.userCode = '';
    this.ascUq = '';
    this.clearResults();
    
    // 年月を基準年月（遷移元の年月）にリセット
    this.selectedYearMonth = this.referenceYearMonth || this.getDefaultYearMonth();
    this.generateYearMonthList();
  }

  // 年月リストを生成（今年-3年の1月から今月まで、降順）
  generateYearMonthList(): void {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const startYear = currentYear - 3;

    const list: string[] = [];
    
    for (let year = currentYear; year >= startYear; year--) {
      const maxMonth = year === currentYear ? currentMonth : 12;
      for (let month = maxMonth; month >= 1; month--) {
        list.push(`${year}${month.toString().padStart(2, '0')}`);
      }
    }

    this.yearMonthList = list;
  }

  // 年月選択変更時
  onYearMonthChange(): void {
    if (this.ascInfo) {
      this.loadTimesheets();
    }
  }

  // タイムシート一覧を取得
  loadTimesheets(): void {
    if (!this.ascInfo) return;

    // 前回の表示状態を確実にリセット
    this.showMessage = false;
    this.showLinks = false;
    this.showDetail = false;
    this.message = '';
    this.selectedTimesheet = null;
    this.timesheetList = [];
    this.lineItems = [];
    this.summary = null;
    this.comments = null;

    this.tsInfoService.getTimesheets(this.ascInfo.userId, this.selectedYearMonth).subscribe({
      next: (timesheets) => {
        if (timesheets.length === 0) {
          const year = parseInt(this.selectedYearMonth.substring(0, 4));
          const month = parseInt(this.selectedYearMonth.substring(4, 6));
          this.message = `${year}年${month}月のタイムシートはありません。`;
          this.showMessage = true;
          this.showLinks = false;
          this.showDetail = false;
        } else if (timesheets.length === 1) {
          // 1件のみ：詳細を直接表示、リンクエリアは非表示
          this.showMessage = false;
          this.showLinks = false;
          this.loadTimesheetDetail(timesheets[0].timesheetId);
        } else {
          // 複数件：リンクリストを表示し、最初のタイムシートの詳細も表示
          this.timesheetList = timesheets;
          this.showMessage = false;
          this.showLinks = true;
          this.loadTimesheetDetail(timesheets[0].timesheetId);
        }
      },
      error: (error) => {
        const year = parseInt(this.selectedYearMonth.substring(0, 4));
        const month = parseInt(this.selectedYearMonth.substring(4, 6));
        this.message = `${year}年${month}月のタイムシートはありません。`;
        this.showMessage = true;
        this.showLinks = false;
        this.showDetail = false;
      }
    });
  }

  // リンクをクリックしてタイムシート詳細を表示
  selectTimesheet(timesheetId: number): void {
    // リンクエリアは表示したまま、詳細だけを切り替える
    this.loadTimesheetDetail(timesheetId);
  }

  // タイムシート詳細を読み込み
  loadTimesheetDetail(timesheetId: number): void {
    // 概要情報
    this.tsInfoService.getTimesheetOverview(timesheetId).subscribe({
      next: (overview) => {
        this.selectedTimesheet = overview;
        this.showDetail = true;
        // 登録番号テキストボックスが未設定の場合は概要情報からセット
        if (!this.userCode) {
          this.userCode = overview.userCode;
        }
      },
      error: () => { /* 概要取得失敗時は詳細エリアを表示しない */ }
    });

    // 明細情報
    this.tsInfoService.getTimesheetLineItems(timesheetId).subscribe({
      next: (items) => {
        this.lineItems = items;
      },
      error: () => { this.lineItems = []; }
    });

    // サマリー情報
    this.tsInfoService.getTimesheetSummary(timesheetId).subscribe({
      next: (summary) => {
        this.summary = summary;
      },
      error: () => { this.summary = null; }
    });

    // コメント情報
    this.tsInfoService.getTimesheetComments(timesheetId).subscribe({
      next: (comments) => {
        this.comments = comments;
      },
      error: () => { this.comments = null; }
    });
  }

  // 結果をクリア
  clearResults(): void {
    this.ascInfo = null;
    this.timesheetList = [];
    this.selectedTimesheet = null;
    this.lineItems = [];
    this.summary = null;
    this.comments = null;
    this.showMessage = false;
    this.showLinks = false;
    this.showDetail = false;
    this.message = '';
  }

  // デフォルト年月を返す（平日5日目＋月別オフセット以降なら当月、それより前は先月）yyyyMM形式
  private getDefaultYearMonth(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-based
    const day = today.getDate();

    // 月ごとの加算日数（0-based月インデックス）
    const monthOffset: { [key: number]: number } = {
      0:  3,  // 1月 +3
      4:  3,  // 5月 +3
      10: 1,  // 11月 +1
    };
    const offset = monthOffset[month] ?? 0;

    // その月の平日5日目を求める
    let weekdayCount = 0;
    let fifthWeekdayDate = 0;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const dow = new Date(year, month, d).getDay();
      if (dow !== 0 && dow !== 6) {
        weekdayCount++;
        if (weekdayCount === 5) {
          fifthWeekdayDate = d;
          break;
        }
      }
    }

    // 閾値 = 平日5日目 + オフセット。閾値以降なら当月、それより前なら先月
    const threshold = fifthWeekdayDate + offset;
    if (day >= threshold) {
      return `${year}${(month + 1).toString().padStart(2, '0')}`;
    } else {
      const prevMonth = month === 0 ? 12 : month;
      const prevYear  = month === 0 ? year - 1 : year;
      return `${prevYear}${prevMonth.toString().padStart(2, '0')}`;
    }
  }

  // 日付をyyyyMM形式にフォーマット
  formatYearMonth(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${year}${month}`;
  }

  // yyyyMM形式を「2025年11月」形式に変換
  formatYearMonthDisplay(yearMonth: string): string {
    if (!yearMonth || yearMonth.length !== 6) {
      return '';
    }
    const year = yearMonth.substring(0, 4);
    const month = parseInt(yearMonth.substring(4, 6), 10);
    return `${year}年${month}月`;
  }

  // 小数時間をHH:MM形式に変換（例: 39.95 → "39:57"）
  // Math.round(decimal * 60) で分を正確に復元してからフォーマット
  toHHMM(decimalHours: number | null | undefined): string {
    if (decimalHours == null) return '';
    const totalMinutes = Math.round(decimalHours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}:${m.toString().padStart(2, '0')}`;
  }

  // CloseDate (0=末日, 15=15日) を「締日末日」「締日15日」に変換
  formatCloseDate(closeDate: number): string {
    if (closeDate === 15) {
      return '締日15日';
    }
    return '締日末日';
  }

  // 全角数字を半角に変換
  private toHalfWidth(str: string): string {
    return str.replace(/[０-９]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
    });
  }

  // ナビゲーションメソッド
  onJobOrderClick(jobOrderId: number): void {
    this.navigateToJobOrder.emit(jobOrderId);
  }

  onAscUqClick(userId: number): void {
    this.navigateToAsc.emit({ ascUq: userId.toString() });
  }

  onCustomerClick(customerOfficeId: number): void {
    this.navigateToCustomer.emit(customerOfficeId);
  }

  onUserCodeClick(userCode: string): void {
    this.navigateToAsc.emit({ userCode });
  }

  // 外部からの呼び出し用の公開メソッド
  public loadByAscUq(ascUq: string, userCode?: string, yearMonth?: string, timesheetId?: number): void {
    // ASC UQを設定（登録番号はASC情報取得後にセット）
    this.ascUq = ascUq;
    this.userCode = userCode || '';
    
    // 年月をフォーマット
    const targetYearMonth = yearMonth ? yearMonth.replace(/-/g, '') : null;
    
    // ASC情報を直接取得（onDisplayは呼ばない）
    const userId = parseInt(this.ascUq);
    
    this.tsInfoService.getAscInfo(undefined, userId).subscribe({
      next: (asc) => {
        if (!asc) {
          this.ascUq = ''; // 存在しないユーザーのためクリア（タブ切替時の自動再検索を防止）
          this.message = 'この番号のASCは存在しません。';
          this.showMessage = true;
          return;
        }

        this.ascInfo = asc;
        this.userCode = asc.userCode; // ASC登録番号をテキストボックスにセット
        this.ascUq = '';           // スタッフ番号(Unique)欄はクリア
        this.showMessage = false;

        // DB から年月リストを取得（未来月を含む）
        this.tsInfoService.getYearMonthList(asc.userId).subscribe({
          next: (dbList) => {
            if (dbList && dbList.length > 0) {
              this.yearMonthList = dbList;
            } else {
              this.generateYearMonthList();
            }

            // targetYearMonth が指定されている場合は必ずセット（未来月でもリストに追加）
            if (targetYearMonth) {
              if (!this.yearMonthList.includes(targetYearMonth)) {
                this.yearMonthList = [targetYearMonth, ...this.yearMonthList];
              }
              this.selectedYearMonth = targetYearMonth;
            } else {
              this.selectedYearMonth = this.yearMonthList[0] ?? this.getDefaultYearMonth();
            }
            this.referenceYearMonth = this.selectedYearMonth;
            this.cdr.detectChanges();

            this.loadTimesheets();

            if (timesheetId) {
              setTimeout(() => {
                this.selectTimesheet(timesheetId);
              }, 500);
            }
          },
          error: () => {
            // 失敗時は固定リストにフォールバック
            this.generateYearMonthList();
            if (targetYearMonth) {
              if (!this.yearMonthList.includes(targetYearMonth)) {
                this.yearMonthList = [targetYearMonth, ...this.yearMonthList];
              }
              this.selectedYearMonth = targetYearMonth;
            } else {
              this.selectedYearMonth = this.getDefaultYearMonth();
            }
            this.referenceYearMonth = this.selectedYearMonth;
            this.cdr.detectChanges();

            this.loadTimesheets();

            if (timesheetId) {
              setTimeout(() => {
                this.selectTimesheet(timesheetId);
              }, 500);
            }
          }
        });
      },
      error: (error) => {
        this.ascUq = ''; // エラー時もクリア（タブ切替時の自動再検索を防止）
        this.message = 'この番号のASCは存在しません。';
        this.showMessage = true;
      }
    });
  }

  public loadByTimesheetId(timesheetId: number, yearMonth?: string, userCode?: string): void {
    // スタッフ番号をセット、スタッフ番号(Unique)はクリア
    this.userCode = userCode || '';
    this.ascUq = '';

    // TimesheetID が指定されている場合：
    // 1) overview からスタッフIDを取得
    // 2) DB上の年月リスト（未来月含む）を取得して yearMonthList にセット
    // 3) 指定年月を選択
    // 4) 同月のタイムシート一覧を取得してリンク表示
    // 5) 指定 TimesheetID の明細を表示
    this.tsInfoService.getTimesheetOverview(timesheetId).subscribe({
      next: (overview) => {
        if (!overview) {
          // overview が取得できなければ従来フォールバック
          this.generateYearMonthList();
          this.selectedYearMonth = this.getDefaultYearMonth();
          this.referenceYearMonth = this.selectedYearMonth;
          this.selectTimesheet(timesheetId);
          return;
        }

        // userCode を画面にセット
        this.userCode = overview.userCode || userCode || '';
        const staffId = overview.userId; // TimesheetOverview.userId = staffId

        // ascInfo をセット（年月ドロップダウン再選択時の loadTimesheets に必要）
        this.ascInfo = {
          userId: staffId,
          userName: overview.userName ?? '',
          userCode: overview.userCode ?? ''
        };

        // DB から年月リストを取得
        this.tsInfoService.getYearMonthList(staffId).subscribe({
          next: (dbList) => {
            // yearMonth 引数をノーマライズ（yyyy-MM → yyyyMM）
            const argYm = yearMonth ? yearMonth.replace(/-/g, '') : null;

            if (dbList && dbList.length > 0) {
              this.yearMonthList = dbList;
            } else {
              // DB リストが空なら固定リストにフォールバック
              this.generateYearMonthList();
            }

            // 選択年月を決定
            // argYm が指定されている場合は必ずそれを使う（未来月でもリストに追加してセット）
            let targetYearMonth: string;
            if (argYm) {
              if (!this.yearMonthList.includes(argYm)) {
                this.yearMonthList = [argYm, ...this.yearMonthList];
              }
              targetYearMonth = argYm;
            } else {
              targetYearMonth = this.yearMonthList[0] ?? this.getDefaultYearMonth();
            }
            this.selectedYearMonth = targetYearMonth;
            this.referenceYearMonth = targetYearMonth;
            this.cdr.detectChanges();

            // 同月のタイムシート一覧を取得してリンク表示
            this.tsInfoService.getTimesheets(staffId, targetYearMonth).subscribe({
              next: (timesheets) => {
                if (timesheets.length > 1) {
                  this.timesheetList = timesheets;
                  this.showLinks = true;
                } else {
                  this.showLinks = false;
                }
                // 指定 TimesheetID の明細を表示
                this.loadTimesheetDetail(timesheetId);
              },
              error: () => {
                this.showLinks = false;
                this.loadTimesheetDetail(timesheetId);
              }
            });
          },
          error: () => {
            // 年月リスト取得失敗時は固定リストにフォールバック
            this.generateYearMonthList();
            const argYm = yearMonth ? yearMonth.replace(/-/g, '') : null;
            // argYm が指定されていれば必ずセット（未来月でもリストに追加）
            if (argYm) {
              if (!this.yearMonthList.includes(argYm)) {
                this.yearMonthList = [argYm, ...this.yearMonthList];
              }
              this.selectedYearMonth = argYm;
            } else {
              this.selectedYearMonth = this.getDefaultYearMonth();
            }
            this.referenceYearMonth = this.selectedYearMonth;
            this.cdr.detectChanges();
            this.loadTimesheetDetail(timesheetId);
          }
        });
      },
      error: () => {
        // overview 取得失敗時は従来動作にフォールバック
        this.generateYearMonthList();
        const targetYearMonth = yearMonth ? yearMonth.replace(/-/g, '') : null;
        if (targetYearMonth && this.yearMonthList.includes(targetYearMonth)) {
          this.selectedYearMonth = targetYearMonth;
          this.referenceYearMonth = targetYearMonth;
        } else {
          this.selectedYearMonth = this.getDefaultYearMonth();
          this.referenceYearMonth = this.selectedYearMonth;
        }
        this.cdr.detectChanges();
        if (userCode) {
          this.tsInfoService.getAscInfo(userCode, undefined).subscribe({
            next: (asc) => { if (asc) this.ascInfo = asc; },
            error: () => {}
          });
        }
        this.selectTimesheet(timesheetId);
      }
    });
  }

  // PDF作成
  downloadTimesheetPdf(): void {
    if (!this.selectedTimesheet?.timesheetId) {
      return;
    }

    this.timesheetService.getMprepPdf(this.selectedTimesheet.timesheetId)
      .subscribe({
        next: (res: any) => {
          const blob = new Blob([res.body ?? res], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = fileURL;
          const ym = this.selectedYearMonth;
          const yearMonth = ym.length === 6 ? `${ym.slice(0, 4)}-${ym.slice(4)}` : ym;
          const userCode = this.userCode || this.selectedTimesheet?.userCode || '';
          link.download = `ledger-${yearMonth}-${userCode}.pdf`;
          link.click();
          URL.revokeObjectURL(fileURL);
        },
        error: (err: any) => {
          console.log(err);
          if (err.status == 401) {
            this.authService.logout();
            return;
          }
        }
      });
  }

  // 代理申請・代理承認ボタンを表示するかどうか
  showProxyButtons(): boolean {
    // AdminLevel が 3 未満の場合は表示しない
    if (this.adminLevel < 3) {
      return false;
    }

    // ReadWrite が false の場合は表示しない
    if (!this.readWrite) {
      return false;
    }

    // タイムシートが選択されていない場合は表示しない
    if (!this.selectedTimesheet) {
      return false;
    }

    // タイムシート明細がない場合は表示しない
    if (!this.lineItems || this.lineItems.length === 0) {
      return false;
    }

    // タイムシート明細の年月を取得（t0フィールド）
    const timesheetYearMonth = this.lineItems[0].t0;
    if (!timesheetYearMonth) {
      return false;
    }

    // 前月以降（前月・今月・翌月以降の未来月）であれば表示
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastYearMonth = this.formatYearMonth(lastMonth);

    return timesheetYearMonth >= lastYearMonth;
  }

  // 承認差戻ボタンがクリックされた時
  onApprovalReturn(): void {
    if (!this.selectedTimesheet) {
      return;
    }

    const timesheetId = this.selectedTimesheet.timesheetId;
    const userId = this.selectedTimesheet.userId;
    
    // TimesheetIDとUserIDを暗号化
    const encryptedTimesheetId = Base64UrlService.encode(
      this.encryptionService.encryptionAES(timesheetId.toString())
    );
    const encryptedUserId = Base64UrlService.encode(
      this.encryptionService.encryptionAES(userId.toString())
    );
    
    // 代理処理タブで承認差戻を表示
    this.proxyRequested.emit({ operation: 'return', encryptedId: encryptedTimesheetId, encryptedUserId: encryptedUserId });
  }

  // 代理申請ボタンがクリックされた時
  onProxySubmission(): void {
    if (!this.selectedTimesheet) {
      return;
    }

    const timesheetId = this.selectedTimesheet.timesheetId;
    const userId = this.selectedTimesheet.userId;
    
    // TimesheetIDとUserIDを暗号化
    const encryptedTimesheetId = Base64UrlService.encode(
      this.encryptionService.encryptionAES(timesheetId.toString())
    );
    const encryptedUserId = Base64UrlService.encode(
      this.encryptionService.encryptionAES(userId.toString())
    );
    
    // 代理処理タブで代理申請を表示
    this.proxyRequested.emit({ operation: 'submit', encryptedId: encryptedTimesheetId, encryptedUserId: encryptedUserId });
  }

  // 代理承認ボタンがクリックされた時
  onProxyApproval(): void {
    if (!this.selectedTimesheet) {
      return;
    }

    const timesheetId = this.selectedTimesheet.timesheetId;
    const userId = this.selectedTimesheet.userId;
    
    // TimesheetIDとUserIDを暗号化
    const encryptedTimesheetId = Base64UrlService.encode(
      this.encryptionService.encryptionAES(timesheetId.toString())
    );
    const encryptedUserId = Base64UrlService.encode(
      this.encryptionService.encryptionAES(userId.toString())
    );
    
    // 代理処理タブで代理承認を表示
    this.proxyRequested.emit({ operation: 'approve', encryptedId: encryptedTimesheetId, encryptedUserId: encryptedUserId });
  }
}
